/**
 * Dossiers CEE — liste avec pipeline, filtres et affichage adapté
 * MaPrimeAdapt' (colonnes dédiées, cartes mobile).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { FolderOpen, Search, ArrowRight, Phone, Mail, MapPin, Calendar, Trash2, RotateCcw, Archive, ArrowUp, ArrowDown, ArrowUpDown, X, Mails, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import ConfirmDialog from '@/components/ConfirmDialog';

const STATUS_LABELS = {
  nouveau: 'Nouveau',
  prequalification: 'Préqualification',
  a_verifier: 'À vérifier',
  sans_reponse: 'Sans réponse',
  hors_criteres: 'Hors critères déclarés',
  offre_engagement: 'Offre & engagement',
  travaux: 'Travaux',
  pieces_a_completer: 'Pièces à compléter',
  controle_depot: 'Contrôle & dépôt',
  accepte: 'Accepté',
  refuse: 'Refusé',
  paye: 'Payé',
};

const STATUS_COLORS = {
  nouveau: 'bg-indigo-100 text-indigo-700',
  prequalification: 'bg-emerald-100 text-emerald-700',
  a_verifier: 'bg-amber-100 text-amber-800',
  sans_reponse: 'bg-slate-200 text-slate-700',
  hors_criteres: 'bg-red-100 text-red-700',
  offre_engagement: 'bg-blue-100 text-blue-700',
  travaux: 'bg-amber-100 text-amber-700',
  pieces_a_completer: 'bg-orange-100 text-orange-700',
  controle_depot: 'bg-purple-100 text-purple-700',
  accepte: 'bg-emerald-100 text-emerald-700',
  refuse: 'bg-red-100 text-red-700',
  paye: 'bg-teal-100 text-teal-700',
};

const QUESTIONNAIRE_LABELS = {
  not_sent: 'Non envoyé',
  sent: 'Envoyé',
  responded: 'Répondu',
};

const QUESTIONNAIRE_COLORS = {
  not_sent: 'bg-slate-100 text-slate-600',
  sent: 'bg-blue-100 text-blue-700',
  responded: 'bg-emerald-100 text-emerald-700',
};

const isMpa = (d) => d.aide_type === 'maprimeadapt';
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—';
const fmtEUR = (v) => v == null ? '—' : new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
const sourceLabel = (d) => {
  if (d.source !== 'meta_ads_instant_form') return null;
  const p = d.source_platform;
  return p ? `Meta Ads · ${p}` : 'Meta Ads';
};

export default function CeeDossiers() {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [dossiers, setDossiers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterAideType, setFilterAideType] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [search, setSearch] = useState('');
  // V3.1p-fix1 — Filtres additionnels (client-side)
  const [filterAmo, setFilterAmo] = useState('');          // amo_id | 'none' | ''
  const [filterCp, setFilterCp] = useState(sp.get('cp') || '');  // V3.1p-fix3: init depuis URL (?cp=)
  const [filterPeriod, setFilterPeriod] = useState('');    // '7' | '30' | '90' | ''
  const [filterDelegataire, setFilterDelegataire] = useState('');
  // V3.1p-fix1 — Tri (client-side)
  const [sortKey, setSortKey] = useState('date_desc');     // date_desc | date_asc | prime_desc | prime_asc | nom_asc | cp_asc
  const [loading, setLoading] = useState(true);
  const [showTrash, setShowTrash] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busyAction, setBusyAction] = useState(false);
  const [amos, setAmos] = useState([]);
  // V3.1q — scan email manuel
  const [scanning, setScanning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (showTrash) params.trash = true;
      if (filterStatus) params.status = filterStatus;
      if (filterAideType) params.aide_type = filterAideType;
      if (filterSource) params.source = filterSource;
      if (search) params.search = search;
      const r = await api.get('/cee/dossiers', { params });
      setDossiers(r.data.dossiers || []);
      setStatuses(r.data.statuses || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur chargement dossiers');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ },
    [filterStatus, filterAideType, filterSource, showTrash]);

  // Charge la liste d'AMO pour le filtre
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/cee/amos', { params: { active_only: false } });
        setAmos(r.data?.items || []);
      } catch (_) { /* silencieux */ }
    })();
  }, []);

  // V3.1p-fix3 — Synchronise filterCp ↔ URL (?cp=XX) pour deep-link + back/forward
  useEffect(() => {
    const current = sp.get('cp') || '';
    if (filterCp !== current) {
      const next = new URLSearchParams(sp);
      if (filterCp) next.set('cp', filterCp);
      else next.delete('cp');
      setSp(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCp]);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setBusyAction(true);
    try {
      await api.delete(`/cee/dossiers/${confirmDelete.dossier_id}`);
      toast.success(`Dossier déplacé dans la corbeille`);
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur suppression');
    } finally { setBusyAction(false); }
  };

  const handleRestore = async (d) => {
    setBusyAction(true);
    try {
      await api.post(`/cee/dossiers/${d.dossier_id}/restore`);
      toast.success('Dossier restauré');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur restauration');
    } finally { setBusyAction(false); }
  };

  // V3.1p-fix1 — Pipeline client-side : filtres AMO/CP/période/délégataire + tri.
  const filteredSorted = useMemo(() => {
    const now = Date.now();
    const periodMs = filterPeriod ? parseInt(filterPeriod, 10) * 86400000 : 0;
    const cpPrefix = (filterCp || '').replace(/\D/g, '');
    const list = dossiers.filter((d) => {
      // AMO
      if (filterAmo === 'none') {
        if (d.amo_id) return false;
      } else if (filterAmo) {
        if (d.amo_id !== filterAmo) return false;
      }
      // CP
      if (cpPrefix) {
        const dcp = ((d.beneficiaire?.code_postal || d.beneficiaire?.cp_ville || '') + '').replace(/\D/g, '');
        if (!dcp.startsWith(cpPrefix)) return false;
      }
      // Période (basée sur imported_at ou created_at)
      if (periodMs) {
        const iso = d.imported_at || d.created_at;
        if (!iso) return false;
        const t = new Date(iso).getTime();
        if (!t || (now - t) > periodMs) return false;
      }
      // Délégataire
      if (filterDelegataire) {
        if ((d.selected_delegataire?.delegataire_nom || '') !== filterDelegataire) return false;
      }
      return true;
    });

    // Tri
    const sorted = [...list].sort((a, b) => {
      switch (sortKey) {
        case 'date_asc':
          return new Date(a.imported_at || a.created_at || 0) - new Date(b.imported_at || b.created_at || 0);
        case 'prime_desc':
          return (b.prime_eur_snapshot ?? -1) - (a.prime_eur_snapshot ?? -1);
        case 'prime_asc':
          return (a.prime_eur_snapshot ?? Infinity) - (b.prime_eur_snapshot ?? Infinity);
        case 'nom_asc': {
          const na = (a.beneficiaire?.nom_prenom || a.beneficiaire?.nom || '').toLowerCase();
          const nb = (b.beneficiaire?.nom_prenom || b.beneficiaire?.nom || '').toLowerCase();
          return na.localeCompare(nb, 'fr');
        }
        case 'cp_asc': {
          const ca = (a.beneficiaire?.code_postal || a.beneficiaire?.cp_ville || '').toString();
          const cb = (b.beneficiaire?.code_postal || b.beneficiaire?.cp_ville || '').toString();
          return ca.localeCompare(cb);
        }
        case 'date_desc':
        default:
          return new Date(b.imported_at || b.created_at || 0) - new Date(a.imported_at || a.created_at || 0);
      }
    });
    return sorted;
  }, [dossiers, filterAmo, filterCp, filterPeriod, filterDelegataire, sortKey]);

  // Liste unique des délégataires (pour le select)
  const delegataireOptions = useMemo(() => {
    const set = new Set();
    dossiers.forEach((d) => {
      const n = d.selected_delegataire?.delegataire_nom;
      if (n) set.add(n);
    });
    return [...set].sort();
  }, [dossiers]);

  const grouped = useMemo(() => {
    const g = {};
    filteredSorted.forEach((d) => {
      g[d.status] = g[d.status] || [];
      g[d.status].push(d);
    });
    return g;
  }, [filteredSorted]);

  const activeFiltersCount = [filterAmo, filterCp, filterPeriod, filterDelegataire]
    .filter((v) => v && v !== '').length;

  const resetExtraFilters = () => {
    setFilterAmo(''); setFilterCp(''); setFilterPeriod(''); setFilterDelegataire('');
    setSortKey('date_desc');
  };

  // Si on filtre explicitement MaPrimeAdapt', on cache les colonnes CEE
  const mpaOnly = filterAideType === 'maprimeadapt';
  const mixedView = !mpaOnly; // Tous types ou filtre CEE pur

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6" data-testid="cee-dossiers-page">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">
            {showTrash ? 'Corbeille — Dossiers CEE' : 'Dossiers CEE'}
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {showTrash
              ? 'Dossiers déplacés dans la corbeille. Les leads Meta restent exclus de la synchronisation pour éviter leur recréation.'
              : 'Suivi du pipeline — CEE et MaPrimeAdapt\'.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              setScanning(true);
              try {
                const r = await api.post('/mpa-email-scan/run', { limit: 50 });
                const { created, matched, skipped_duplicate, error } = r.data || {};
                if (error) {
                  toast.error(`Scan échoué : ${error}`);
                } else if (created > 0) {
                  toast.success(
                    `${created} nouveau${created > 1 ? 'x' : ''} dossier${created > 1 ? 's' : ''} MaPrimeAdapt' créé${created > 1 ? 's' : ''}`,
                    { description: `${matched} email${matched > 1 ? 's' : ''} trouvé${matched > 1 ? 's' : ''} · ${skipped_duplicate} déjà traité${skipped_duplicate > 1 ? 's' : ''}`, duration: 6000 },
                  );
                  await load();
                } else {
                  toast.info(`Scan OK — aucun nouveau dossier`, {
                    description: `${matched} email${matched > 1 ? 's MPA trouvés' : ' MPA trouvé'}, ${skipped_duplicate} déjà traité${skipped_duplicate > 1 ? 's' : ''}`,
                  });
                }
              } catch (e) {
                toast.error(e?.response?.data?.detail || 'Scan échoué');
              } finally { setScanning(false); }
            }}
            disabled={scanning}
            data-testid="mpa-email-scan-btn"
            className="border-blue-300 text-blue-700 hover:bg-blue-50"
          >
            {scanning
              ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              : <Mails className="w-3.5 h-3.5 mr-1.5" />}
            Scanner les emails
          </Button>
          <Button
            variant={showTrash ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShowTrash((v) => !v)}
            data-testid="toggle-trash-btn"
            className={showTrash ? 'bg-slate-900 hover:bg-slate-800' : ''}
          >
            <Archive className="w-3.5 h-3.5 mr-1.5" />
            {showTrash ? 'Retour à la liste' : 'Corbeille'}
          </Button>
        </div>
      </div>

      <Card className="p-3 sm:p-4 flex flex-wrap gap-2 sm:gap-3 items-center border-slate-200">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Rechercher…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
            className="pl-9"
            data-testid="cee-dossier-search"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="border border-slate-200 rounded-md px-3 py-2 text-sm"
          data-testid="cee-dossier-status-filter"
        >
          <option value="">Tous statuts</option>
          {statuses.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
        </select>
        <select
          value={filterAideType}
          onChange={(e) => setFilterAideType(e.target.value)}
          className="border border-slate-200 rounded-md px-3 py-2 text-sm"
          data-testid="cee-dossier-type-filter"
        >
          <option value="">Tous types</option>
          <option value="cee">CEE</option>
          <option value="maprimeadapt">MaPrimeAdapt'</option>
        </select>
        <select
          value={filterSource}
          onChange={(e) => setFilterSource(e.target.value)}
          className="border border-slate-200 rounded-md px-3 py-2 text-sm"
          data-testid="cee-dossier-source-filter"
        >
          <option value="">Toutes sources</option>
          <option value="meta_ads_instant_form">Meta Ads — Formulaire</option>
          <option value="cee_conversion">Conversion CEE</option>
        </select>
        {/* V3.1p-fix1 — Filtres additionnels */}
        <select
          value={filterAmo}
          onChange={(e) => setFilterAmo(e.target.value)}
          className="border border-slate-200 rounded-md px-3 py-2 text-sm"
          data-testid="cee-dossier-amo-filter"
        >
          <option value="">Tous AMO</option>
          <option value="none">Sans AMO</option>
          {amos.map((a) => (
            <option key={a.amo_id} value={a.amo_id}>{a.nom}</option>
          ))}
        </select>
        {delegataireOptions.length > 0 && (
          <select
            value={filterDelegataire}
            onChange={(e) => setFilterDelegataire(e.target.value)}
            className="border border-slate-200 rounded-md px-3 py-2 text-sm"
            data-testid="cee-dossier-delegataire-filter"
          >
            <option value="">Tous délégataires</option>
            {delegataireOptions.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        )}
        <select
          value={filterPeriod}
          onChange={(e) => setFilterPeriod(e.target.value)}
          className="border border-slate-200 rounded-md px-3 py-2 text-sm"
          data-testid="cee-dossier-period-filter"
        >
          <option value="">Toutes dates</option>
          <option value="7">7 derniers jours</option>
          <option value="30">30 derniers jours</option>
          <option value="90">90 derniers jours</option>
          <option value="365">12 derniers mois</option>
        </select>
        <div className="relative w-28">
          <Input
            placeholder="CP"
            value={filterCp}
            onChange={(e) => setFilterCp(e.target.value)}
            maxLength={5}
            data-testid="cee-dossier-cp-filter"
          />
        </div>
        <select
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value)}
          className="border border-slate-200 rounded-md px-3 py-2 text-sm bg-slate-50"
          data-testid="cee-dossier-sort"
        >
          <option value="date_desc">Trier : plus récents</option>
          <option value="date_asc">Trier : plus anciens</option>
          <option value="prime_desc">Trier : prime € ↓</option>
          <option value="prime_asc">Trier : prime € ↑</option>
          <option value="nom_asc">Trier : nom A→Z</option>
          <option value="cp_asc">Trier : CP ↑</option>
        </select>
        {activeFiltersCount > 0 && (
          <Button variant="outline" size="sm" onClick={resetExtraFilters}
                  data-testid="cee-dossier-reset-filters">
            <X className="w-3.5 h-3.5 mr-1" />
            Réinit. ({activeFiltersCount})
          </Button>
        )}
      </Card>

      {loading ? (
        <div className="p-8 text-center text-slate-500">Chargement…</div>
      ) : dossiers.length === 0 ? (
        <Card className="p-12 text-center border-slate-200">
          <FolderOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 mb-2">Aucun dossier pour l'instant.</p>
          <Button variant="outline" className="mt-4" onClick={() => nav('/cee/simulations')}>
            Aller aux simulations
          </Button>
        </Card>
      ) : filteredSorted.length === 0 ? (
        <Card className="p-10 text-center border-slate-200">
          <FolderOpen className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600">Aucun dossier ne correspond aux filtres.</p>
          <Button variant="outline" className="mt-3" onClick={resetExtraFilters}>
            Réinitialiser les filtres
          </Button>
        </Card>
      ) : (
        <>
          {/* Pipeline horizontal (desktop & tablette ≥ md) */}
          <div className="hidden md:grid grid-cols-4 xl:grid-cols-8 gap-3">
            {statuses.map((st) => (
              <div key={st} className="min-h-[120px]" data-testid={`cee-pipeline-col-${st}`}>
                <div className={`px-2 py-1.5 rounded-t text-xs font-semibold ${STATUS_COLORS[st]}`}>
                  {STATUS_LABELS[st]} · {(grouped[st] || []).length}
                </div>
                <div className="border border-t-0 border-slate-200 rounded-b p-2 space-y-2 bg-slate-50 min-h-[100px]">
                  {(grouped[st] || []).map((d) => (
                    <Link
                      key={d.dossier_id}
                      to={`/cee/dossiers/${d.dossier_id}`}
                      className="block bg-white rounded p-2 hover:shadow-sm transition text-xs"
                    >
                      <div className="flex items-center justify-between gap-1">
                        <div className="font-mono text-slate-500">{d.dossier_id.slice(-8)}</div>
                        {isMpa(d) && (
                          <span className="text-[9px] px-1 py-0.5 rounded bg-indigo-100 text-indigo-700 font-semibold"
                                data-testid={`cee-dossier-type-badge-${d.dossier_id}`}>
                            MPA
                          </span>
                        )}
                      </div>
                      <div className="font-semibold text-slate-900 truncate">
                        {isMpa(d)
                          ? (d.beneficiaire?.nom_prenom || d.beneficiaire?.nom || '(Prospect)')
                          : d.fiche_code}
                      </div>
                      <div className="text-slate-500 truncate">
                        {isMpa(d)
                          ? (d.beneficiaire?.email || d.beneficiaire?.telephone || 'Sans contact')
                          : (d.beneficiaire?.nom || d.beneficiaire?.raison_sociale || '—')}
                      </div>
                      {sourceLabel(d) && (
                        <div className="mt-1 text-[9px] text-slate-500 truncate">
                          {sourceLabel(d)}
                        </div>
                      )}
                      {!isMpa(d) && d.prime_eur_snapshot != null && (
                        <div className="mt-1 font-bold text-emerald-700">
                          {fmtEUR(d.prime_eur_snapshot)}
                          {d.selected_tarif_type === 'precarite' && (
                            <span className="text-[9px] text-orange-600 ml-1">précarité</span>
                          )}
                        </div>
                      )}
                      {!isMpa(d) && d.selected_delegataire?.delegataire_nom && (
                        <div className="text-[10px] text-slate-500 truncate">
                          {d.selected_delegataire.delegataire_nom}
                        </div>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Cartes mobile (< md) */}
          <div className="md:hidden space-y-3" data-testid="cee-dossier-mobile-cards">
            {filteredSorted.map((d) => (
              <Link
                key={d.dossier_id}
                to={`/cee/dossiers/${d.dossier_id}`}
                className="block"
                data-testid={`cee-dossier-card-${d.dossier_id}`}
              >
                <Card className="p-4 border-slate-200 hover:border-[#1C49A6] transition active:bg-slate-50">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 truncate">
                        {isMpa(d)
                          ? (d.beneficiaire?.nom_prenom || '(Prospect)')
                          : (d.beneficiaire?.nom || d.fiche_code)}
                      </div>
                      <div className="text-[11px] font-mono text-slate-400 mt-0.5">
                        {d.dossier_id}
                      </div>
                      {isMpa(d) && (
                        <div className="mt-1 inline-flex items-center gap-0 rounded border border-amber-200 bg-amber-50 text-[10px] overflow-hidden">
                          <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 font-bold border-r border-amber-200">AMO</span>
                          <span className="px-2 py-0.5 text-amber-900 font-medium">
                            {d.amo_nom || <span className="italic text-amber-500">à définir</span>}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      {isMpa(d) ? (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-semibold">
                          MaPrimeAdapt'
                        </span>
                      ) : (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold">
                          CEE
                        </span>
                      )}
                      <Badge className={`${STATUS_COLORS[d.status]} border-0 text-[10px]`}>
                        {STATUS_LABELS[d.status]}
                      </Badge>
                    </div>
                  </div>

                  <div className="space-y-1 text-xs text-slate-600">
                    {d.beneficiaire?.telephone && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <span>{d.beneficiaire.telephone}</span>
                      </div>
                    )}
                    {d.beneficiaire?.email && (
                      <div className="flex items-center gap-2 min-w-0">
                        <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <span className="truncate">{d.beneficiaire.email}</span>
                      </div>
                    )}
                    {d.beneficiaire?.code_postal && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <span>{d.beneficiaire.code_postal}</span>
                      </div>
                    )}
                    {d.imported_at && (
                      <div className="flex items-center gap-2 text-slate-500">
                        <Calendar className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <span>Reçu le {fmtDate(d.imported_at)}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                    {sourceLabel(d) ? (
                      <span className="text-[10px] text-slate-500">{sourceLabel(d)}</span>
                    ) : (
                      <span className="text-[10px] text-slate-400">—</span>
                    )}
                    <div className="flex items-center gap-2">
                      {isMpa(d) ? (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${QUESTIONNAIRE_COLORS[d.questionnaire_status || 'not_sent']}`}>
                          Préqualif : {QUESTIONNAIRE_LABELS[d.questionnaire_status || 'not_sent']}
                        </span>
                      ) : d.prime_eur_snapshot != null ? (
                        <span className="font-bold text-emerald-700 text-sm">
                          {fmtEUR(d.prime_eur_snapshot)}
                        </span>
                      ) : null}
                      {showTrash ? (
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRestore(d); }}
                          className="p-1 rounded hover:bg-emerald-50"
                          data-testid={`cee-dossier-restore-mobile-${d.dossier_id}`}
                        >
                          <RotateCcw className="w-4 h-4 text-emerald-600" />
                        </button>
                      ) : (
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setConfirmDelete(d); }}
                          className="p-1 rounded hover:bg-red-50"
                          data-testid={`cee-dossier-delete-mobile-${d.dossier_id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      )}
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>

          {/* Tableau desktop (≥ md) — colonnes adaptées */}
          <Card className="hidden md:block border-slate-200 overflow-hidden">
            <div className="px-4 py-2 border-b border-slate-100 bg-slate-50 flex items-center justify-between text-xs text-slate-500">
              <span data-testid="cee-dossier-result-count">
                <b>{filteredSorted.length}</b> dossier{filteredSorted.length > 1 ? 's' : ''}
                {filteredSorted.length !== dossiers.length && (
                  <> sur {dossiers.length}</>
                )}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="cee-dossier-table">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="text-left px-3 py-2.5 font-semibold text-slate-600 text-xs uppercase">Dossier</th>
                    <th className="text-left px-3 py-2.5 font-semibold text-slate-600 text-xs uppercase">Type</th>
                    <SortableTh label="Contact" active={sortKey.startsWith('nom')}
                                 dir={sortKey === 'nom_asc' ? 'asc' : null}
                                 onClick={() => setSortKey(sortKey === 'nom_asc' ? 'date_desc' : 'nom_asc')} />
                    <th className="text-left px-3 py-2.5 font-semibold text-slate-600 text-xs uppercase">Téléphone</th>
                    <th className="text-left px-3 py-2.5 font-semibold text-slate-600 text-xs uppercase">Email</th>
                    <SortableTh label="CP" active={sortKey === 'cp_asc'}
                                 dir={sortKey === 'cp_asc' ? 'asc' : null}
                                 onClick={() => setSortKey(sortKey === 'cp_asc' ? 'date_desc' : 'cp_asc')} />
                    <th className="text-left px-3 py-2.5 font-semibold text-slate-600 text-xs uppercase">Source</th>
                    <SortableTh label="Reçu le" active={sortKey.startsWith('date')}
                                 dir={sortKey === 'date_asc' ? 'asc' : sortKey === 'date_desc' ? 'desc' : null}
                                 onClick={() => setSortKey(sortKey === 'date_desc' ? 'date_asc' : 'date_desc')} />
                    <th className="text-left px-3 py-2.5 font-semibold text-slate-600 text-xs uppercase">Statut</th>
                    {mpaOnly ? (
                      <th className="text-left px-3 py-2.5 font-semibold text-slate-600 text-xs uppercase">Questionnaire</th>
                    ) : (
                      <>
                        <th className="text-left px-3 py-2.5 font-semibold text-slate-600 text-xs uppercase">Fiche CEE</th>
                        <th className="text-left px-3 py-2.5 font-semibold text-slate-600 text-xs uppercase">Délégataire / AMO</th>
                        <th className="text-right px-3 py-2.5 font-semibold text-slate-600 text-xs uppercase">kWh cumac</th>
                        <SortableTh label="Prime €" align="right" accent="emerald"
                                     active={sortKey.startsWith('prime')}
                                     dir={sortKey === 'prime_asc' ? 'asc' : sortKey === 'prime_desc' ? 'desc' : null}
                                     onClick={() => setSortKey(sortKey === 'prime_desc' ? 'prime_asc' : 'prime_desc')} />
                      </>
                    )}
                    <th className="px-3 py-2.5"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredSorted.map((d) => {
                    const mpa = isMpa(d);
                    return (
                    <tr key={d.dossier_id} className="hover:bg-slate-50"
                        data-testid={`cee-dossier-row-${d.dossier_id}`}>
                      <td className="px-3 py-2 font-mono text-xs text-slate-500 whitespace-nowrap">
                        {d.dossier_id.slice(-10)}
                      </td>
                      <td className="px-3 py-2">
                        {mpa ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-semibold">
                            MaPrimeAdapt'
                          </span>
                        ) : (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold">
                            CEE
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-900 whitespace-nowrap max-w-[220px]">
                        <div className="truncate">
                          {d.beneficiaire?.nom_prenom || d.beneficiaire?.nom || d.beneficiaire?.raison_sociale ||
                            <span className="text-slate-300">—</span>}
                        </div>
                        {mpa && (
                          <div className="mt-0.5 inline-flex items-center gap-0 rounded border border-amber-200 bg-amber-50 text-[9px] overflow-hidden"
                               data-testid={`amo-chip-${d.dossier_id}`}>
                            <span className="px-1 py-0.5 bg-amber-100 text-amber-800 font-bold border-r border-amber-200">AMO</span>
                            <span className="px-1.5 py-0.5 text-amber-900 font-medium">
                              {d.amo_nom || <span className="italic text-amber-500">à définir</span>}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-700 whitespace-nowrap">
                        {d.beneficiaire?.telephone ? (
                          <a href={`tel:${d.beneficiaire.telephone}`}
                             onClick={(e) => e.stopPropagation()}
                             className="text-[#1C49A6] hover:underline">
                            {d.beneficiaire.telephone}
                          </a>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2 text-slate-700 max-w-[180px] truncate">
                        {d.beneficiaire?.email || <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2 text-slate-700 whitespace-nowrap">
                        {d.beneficiaire?.code_postal || <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap">
                        {sourceLabel(d) || <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">
                        {fmtDate(d.imported_at || d.created_at)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <Badge className={`${STATUS_COLORS[d.status]} border-0 text-[10px]`}>
                          {STATUS_LABELS[d.status]}
                        </Badge>
                      </td>
                      {mpaOnly ? (
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${QUESTIONNAIRE_COLORS[d.questionnaire_status || 'not_sent']}`}>
                            {QUESTIONNAIRE_LABELS[d.questionnaire_status || 'not_sent']}
                          </span>
                        </td>
                      ) : (
                        <>
                          <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                            {mpa ? <span className="text-slate-300">Non applicable</span>
                              : <>{d.fiche_code} <span className="text-slate-400">v{d.fiche_version}</span></>}
                          </td>
                          <td className="px-3 py-2 text-xs">
                            {mpa
                              ? (d.amo_nom
                                  ? (
                                    <span className="inline-flex items-center gap-0 rounded border border-amber-200 bg-amber-50 overflow-hidden">
                                      <span className="px-1 py-0.5 bg-amber-100 text-amber-800 font-bold text-[9px] border-r border-amber-200">AMO</span>
                                      <span className="px-1.5 py-0.5 text-amber-900 font-medium text-[11px]">{d.amo_nom}</span>
                                    </span>
                                  )
                                  : <span className="italic text-amber-500">AMO à définir</span>)
                              : d.selected_delegataire?.delegataire_nom || <span className="text-slate-300">—</span>}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-xs text-slate-500">
                            {mpa ? <span className="text-slate-300">Non applicable</span>
                              : d.kwh_cumac_snapshot != null
                                ? new Intl.NumberFormat('fr-FR').format(d.kwh_cumac_snapshot)
                                : <span className="text-amber-600">—</span>}
                          </td>
                          <td className="px-3 py-2 text-right font-mono font-bold text-emerald-700">
                            {mpa ? <span className="text-slate-300 font-normal">Non applicable</span>
                              : d.prime_eur_snapshot != null ? fmtEUR(d.prime_eur_snapshot)
                              : <span className="text-slate-300">—</span>}
                          </td>
                        </>
                      )}
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex items-center gap-1">
                          {showTrash ? (
                            <Button variant="ghost" size="sm"
                              onClick={(e) => { e.stopPropagation(); handleRestore(d); }}
                              disabled={busyAction}
                              title="Restaurer"
                              data-testid={`cee-dossier-restore-${d.dossier_id}`}>
                              <RotateCcw className="w-4 h-4 text-emerald-600" />
                            </Button>
                          ) : (
                            <Button variant="ghost" size="sm"
                              onClick={(e) => { e.stopPropagation(); setConfirmDelete(d); }}
                              disabled={busyAction}
                              title="Déplacer dans la corbeille"
                              data-testid={`cee-dossier-delete-${d.dossier_id}`}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm"
                            onClick={() => nav(`/cee/dossiers/${d.dossier_id}`)}
                            data-testid={`cee-dossier-open-${d.dossier_id}`}>
                            <ArrowRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        danger
        title="Déplacer ce dossier dans la corbeille ?"
        description={confirmDelete ? (
          `${confirmDelete.beneficiaire?.nom_prenom || confirmDelete.beneficiaire?.nom || '(sans nom)'}\n` +
          `Réf : ${confirmDelete.dossier_id}\n\n` +
          `• Le dossier sera masqué de la liste principale.\n` +
          `• Les relances / tokens de questionnaire associés sont invalidés.\n` +
          `• Un lead Meta supprimé ne sera PAS recréé à la prochaine synchro.\n` +
          `• Vous pourrez le restaurer depuis la corbeille.`
        ) : ''}
        confirmLabel="Déplacer dans la corbeille"
        busy={busyAction}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
}

// V3.1p-fix1 — Header de colonne cliquable (indicateur asc/desc)
function SortableTh({ label, active, dir, onClick, align = 'left', accent }) {
  const alignCls = align === 'right' ? 'text-right' : 'text-left';
  const color = accent === 'emerald' ? 'text-emerald-700' : 'text-slate-600';
  const Icon = dir === 'asc' ? ArrowUp : dir === 'desc' ? ArrowDown : ArrowUpDown;
  return (
    <th className={`${alignCls} px-3 py-2.5 font-semibold ${color} text-xs uppercase select-none`}>
      <button
        type="button"
        onClick={onClick}
        className={`inline-flex items-center gap-1 hover:text-[#1C49A6] transition-colors ${
          active ? 'text-[#1C49A6]' : ''
        } ${align === 'right' ? 'flex-row-reverse' : ''}`}
      >
        <span>{label}</span>
        <Icon className={`w-3 h-3 ${active ? 'opacity-100' : 'opacity-40'}`} />
      </button>
    </th>
  );
}
